import * as sortLib from "./sortLib"
import { sortDDMMYYYY, getDateFromDDMMYYYY } from "../dateLib/dateLib"

interface ObjectWithDates {
	endDate?: string
	startDate?: string
}

interface ObjectWithReceivedTime {
	receivedTime: number
}

interface ObjectWithDuplicationTime {
	duplicationTime: number
}

interface ObjectWithCreatedAt {
	createdAt: number
}

export function sortByEndDate(a: ObjectWithDates, b: ObjectWithDates): number {
	return sortDDMMYYYY(a.endDate, b.endDate)
}

export function sortByStartDate(a: ObjectWithDates, b: ObjectWithDates): number {
	return sortDDMMYYYY(a.startDate, b.startDate)
}

export function sortByDate(a: ObjectWithDates, b: ObjectWithDates): number {
	return sortLib.sortByEndDate(a, b) || sortLib.sortByStartDate(a, b)
}

export function sortByCreatedAt(a: ObjectWithCreatedAt, b: ObjectWithCreatedAt): number {
	// Handle null/undefined cases
	if (!a?.createdAt && !b?.createdAt) return 0
	if (!a?.createdAt) return 1
	if (!b?.createdAt) return -1

	// Sort by timestamp (newest first)
	return b.createdAt - a.createdAt
}

export function getFirstWhenSorted<T>(array: T[], sortFunc: (a: T, b: T) => number): T {
	//Avoid sorting full array to find first
	function checkWhich(a: T, b: T): T {
		return sortFunc(a, b) <= 0 ? a : b
	}
	return array.reduce(checkWhich, array[0])
}

// export function sortByScoreForVersion(version) {
//     return function sortByScore(a, b) {
//         return ((b?.score?.[version] ?? 0) - (a?.score?.[version] ?? 0))
//     }
// }

export function sortByReceivedTime(a: ObjectWithReceivedTime, b: ObjectWithReceivedTime): number {
	return b.receivedTime - a.receivedTime
}

export function sortByDuplicated(a: ObjectWithDuplicationTime, b: ObjectWithDuplicationTime): number {
	return (b?.duplicationTime ?? 0) - (a?.duplicationTime ?? 0)
}

const baseAlphabeticalComparer = new Intl.Collator().compare

export const alphabeticalComparer = (a: string, b: string): number => baseAlphabeticalComparer(a?.trim?.(), b?.trim?.())

export const naturalSortComparer = (a: string, b: string): number => {
	const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" })
	return collator.compare(a?.trim?.(), b?.trim?.())
}

export function sortByDateTime(a: string, b: string): number {
	// Handle null/undefined cases
	if (!a && !b) return 0
	if (!a) return 1
	if (!b) return -1

	// Split date and time
	const [dateA, timeA] = (a || "").split(",")
	const [dateB, timeB] = (b || "").split(",")

	// Get base dates using existing function
	const baseDateA = getDateFromDDMMYYYY(dateA)
	const baseDateB = getDateFromDDMMYYYY(dateB)

	// Handle invalid dates
	if (isNaN(baseDateA.getTime()) && isNaN(baseDateB.getTime())) return 0
	if (isNaN(baseDateA.getTime())) return 1
	if (isNaN(baseDateB.getTime())) return -1

	// Handle time components
	const hasValidTimeA =
		timeA &&
		timeA
			.trim()
			.split(":")
			.every((part) => !isNaN(Number(part)))
	const hasValidTimeB =
		timeB &&
		timeB
			.trim()
			.split(":")
			.every((part) => !isNaN(Number(part)))

	// If one has valid time and other doesn't, prioritize the one with valid time
	if (hasValidTimeA && !hasValidTimeB) return -1
	if (!hasValidTimeA && hasValidTimeB) return 1
	if (!hasValidTimeA && !hasValidTimeB) {
		// For date-only comparison
		return baseDateA < baseDateB ? 1 : baseDateA > baseDateB ? -1 : 0
	}

	// Both have valid times, add them to dates
	const [hoursA, minutesA, secondsA] = timeA.trim().split(":").map(Number)
	const [hoursB, minutesB, secondsB] = timeB.trim().split(":").map(Number)
	baseDateA.setUTCHours(hoursA, minutesA, secondsA)
	baseDateB.setUTCHours(hoursB, minutesB, secondsB)

	// Standard three-way comparison
	return baseDateA < baseDateB ? 1 : baseDateA > baseDateB ? -1 : 0
}
